import { useLazyQuery } from '@apollo/client';
import { getBackendLanguage } from '@assets/i18n/config';
import type {
  FetchPhysicalAddressQuery,
  FetchPhysicalAddressQueryVariables,
  SearchPhysicalAddressQuery,
  SearchPhysicalAddressQueryVariables,
} from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import LockIcon from '@mui/icons-material/Lock';
import { InputAdornment, type SxProps } from '@mui/material';
import type { Jurisdictions } from '@onevesthq/ov-enums';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Tooltip, useSnackbar } from 'hephaestus/web';
import { kebabCase, throttle } from 'lodash-es';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '../autocomplete/autocomplete';
import {
  FETCH_PHYSICAL_ADDRESS,
  SEARCH_PHYSICAL_ADDRESS,
} from './addressField.queries';
import { dedupeAddressOptionsByIdOrText } from './addressOption.utils';
import { extractPostalCodeFromText, isCompletePostalCode } from './postalCode';

type Props = {
  label?: string;
  addressString?: string;
  setAddress?: (address: Partial<PhysicalAddress>) => void;
  error?: boolean;
  integrationId?: string;
  organizationId?: string;
  modalOpen: boolean;
  errorText?: string;
  disabled?: boolean;
  onFocus?: any;
  onBlur?: any;
  sx?: SxProps;
  infoTooltip?: string;
  onLookupFault?: () => void;
  lockMessage?: string;
  testId?: string;
};

type PlaceType = {
  text: string;
  id: string;
  next?: string;
};

type AddressLookupResult = NonNullable<
  NonNullable<
    FetchPhysicalAddressQuery['fetchPhysicalAddress']
  >['addressDetails']
>;

const CanadaPostAddress = ({
  setAddress,
  label,
  addressString,
  error,
  integrationId,
  organizationId,
  modalOpen = false,
  errorText,
  disabled,
  onFocus,
  sx,
  onBlur,
  infoTooltip,
  onLookupFault,
  lockMessage,
  testId,
}: Props): JSX.Element => {
  const { t } = useTranslation(['pageConfiguration', 'components']);
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState<string>(addressString ?? '');
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const inputRef = useRef<any>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);

  const [searchAddress] = useLazyQuery<
    SearchPhysicalAddressQuery,
    SearchPhysicalAddressQueryVariables
  >(SEARCH_PHYSICAL_ADDRESS);
  const [fetchAddress] = useLazyQuery<
    FetchPhysicalAddressQuery,
    FetchPhysicalAddressQueryVariables
  >(FETCH_PHYSICAL_ADDRESS);

  const SUPPORTED_COUNTRIES: string[] = ['CAN']; // todo: OV-12255
  const SUPPORTED_PROVINCE: string[] = []; // todo: OV-11858

  const handleLookupError = useCallback(
    (callback: (results?: readonly PlaceType[]) => void) => {
      enqueueSnackbar(
        'Failed to fetch address suggestions. Fallback to manual input.',
        { variant: 'error' },
      );
      if (onLookupFault) onLookupFault();
      callback([]);
    },
    [enqueueSnackbar, onLookupFault],
  );

  const fetchAddressSuggestion = useMemo(
    () =>
      throttle(
        async (
          input: string,
          callback: (results?: readonly PlaceType[]) => void,
          lastId?: string,
        ) => {
          if (!integrationId || !organizationId) {
            handleLookupError(callback);
            return;
          }

          try {
            const { data } = await searchAddress({
              variables: {
                input: {
                  integrationId,
                  organizationId,
                  searchTerm: input,
                  language: getBackendLanguage(),
                  maxResults: 8,
                  ...(lastId ? { lastId } : {}),
                },
              },
            });

            if (!data?.searchPhysicalAddress?.suggestions) {
              handleLookupError(callback);
              return;
            }

            const suggestions = data.searchPhysicalAddress.suggestions.map(
              (item) => ({
                id: item.id,
                next: item.next ?? undefined,
                text: item.text,
              }),
            );
            callback(dedupeAddressOptionsByIdOrText(suggestions));
          } catch {
            handleLookupError(callback);
          }
        },
        200,
      ),
    [integrationId, organizationId, searchAddress, handleLookupError],
  );

  useEffect(() => {
    let active = true;
    if (!inputValue) {
      setOptions(value ? [value] : []);
      return undefined;
    }
    if (!hasBeenFocused) return undefined;

    fetchAddressSuggestion(
      inputValue,
      (results?: readonly PlaceType[]) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];
          if (results) {
            newOptions = [...newOptions, ...results];
          }
          setOptions(dedupeAddressOptionsByIdOrText(newOptions));
        }
      },
      value?.id,
    );
    return () => {
      active = false;
    };
  }, [value, inputValue, fetchAddressSuggestion, hasBeenFocused]);

  const fetchAddressById = async (
    selectedOption: PlaceType,
  ): Promise<AddressLookupResult | null> => {
    if (!integrationId || !organizationId) return null;

    try {
      const { data } = await fetchAddress({
        variables: {
          input: {
            integrationId,
            organizationId,
            addressId: selectedOption.id,
            language: getBackendLanguage(),
          },
        },
      });
      return data?.fetchPhysicalAddress?.addressDetails ?? null;
    } catch {
      throw new Error('Failed to fetch address details');
    }
  };

  const validateLocationRestriction = async (
    result: AddressLookupResult,
  ): Promise<boolean> => {
    const countryCode = [
      result.CountryIso2,
      result.CountryIso3,
      result.CountryName,
    ];
    const provinceCode = [
      result.Province,
      result.ProvinceCode,
      result.ProvinceCode,
    ];

    if (
      (SUPPORTED_COUNTRIES ?? []).length > 0 &&
      !SUPPORTED_COUNTRIES.some((x) => countryCode.includes(x))
    ) {
      enqueueSnackbar(t('components:address.error.unSupportedCountry'), {
        variant: 'error',
      });
      return false;
    }

    if (
      (SUPPORTED_PROVINCE ?? []).length > 0 &&
      !SUPPORTED_PROVINCE.some((x) => provinceCode.includes(x))
    ) {
      enqueueSnackbar(t('components:address.error.unSupportedProvince'), {
        variant: 'error',
      });
      return false;
    }

    return true;
  };

  const setLocalAddress = (
    result: AddressLookupResult,
    selectedOption: PlaceType,
  ): Partial<PhysicalAddress> => {
    const localAddress: Partial<PhysicalAddress> = {};
    localAddress.country = result.country as PhysicalAddress['country'];
    localAddress.jurisdiction =
      (result.jurisdiction as Jurisdictions | undefined) ||
      (`${result.country}_${result.ProvinceCode}` as Jurisdictions);
    localAddress.houseNumber = result.houseNumber ?? undefined;
    localAddress.unitNumber = result.unitNumber ?? undefined;
    localAddress.streetName = result.streetName ?? undefined;
    localAddress.city = result.city ?? undefined;
    const selectedPostalCode = extractPostalCodeFromText(selectedOption.text);
    localAddress.postal = isCompletePostalCode(result.postal ?? undefined)
      ? (result.postal ?? undefined)
      : selectedPostalCode;
    localAddress.neighborhood = result.neighborhood ?? undefined;
    return localAddress;
  };

  const handleSelect = async (selectedOption: PlaceType): Promise<void> => {
    if (selectedOption.next === 'Find') {
      const regex = / - \d+ Addresses$/;
      const newText = selectedOption.text.replace(regex, '');
      setValue({ text: newText, id: selectedOption.id });
      setInputValue(newText);
      setOpen(true);
      return;
    }
    setValue({ text: selectedOption.text, id: '' });

    try {
      const result = await fetchAddressById(selectedOption);

      if (!result) return;

      const validate: boolean = await validateLocationRestriction(result);

      if (!validate) {
        setInputValue('');
        return;
      }

      const localAddress = setLocalAddress(result, selectedOption);
      if (setAddress) setAddress(localAddress);
      setOpen(false);
    } catch {
      enqueueSnackbar(
        'Failed to fetch address details. Fallback to manual input.',
        { variant: 'error' },
      );
    }
  };

  const fieldValue = modalOpen ? `${value} ` : value;

  return (
    <Autocomplete
      testId={testId ?? 'canada-post-autocomplete'}
      open={open ?? false}
      label={label ?? ''}
      infoTooltip={infoTooltip}
      error={error}
      errorText={errorText}
      disabled={disabled}
      onFocus={() => {
        setHasBeenFocused(true);
        if (onFocus) onFocus();
      }}
      blurOnSelect
      fullWidth
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.text
      }
      freeSolo
      onBlur={onBlur}
      sx={sx}
      InputProps={{
        inputRef,
        endAdornment: disabled ? (
          <InputAdornment position='end'>
            <Tooltip title={lockMessage ?? t('notEditableMessage')}>
              <Icon icon={LockIcon} size='small' />
            </Tooltip>
          </InputAdornment>
        ) : null,
        disabled,
      }}
      options={[...options]}
      value={open ? fieldValue : addressString}
      onChange={(
        _: React.ChangeEvent<HTMLInputElement>,
        newValue: PlaceType | null,
      ) => {
        setValue(newValue);
        if (newValue) {
          handleSelect(newValue);
        }
      }}
      renderOption={(props: any, option: PlaceType) => (
        <li
          {...props}
          data-testid={
            testId
              ? `${testId}-${kebabCase(option.text)}`
              : `canada-post-autocomplete-${kebabCase(option.text)}`
          }
        >
          {option.text}
        </li>
      )}
      onInputChange={(
        _: React.ChangeEvent<HTMLInputElement>,
        newInputValue: SetStateAction<string>,
      ) => {
        setOpen(!modalOpen);
        setInputValue(newInputValue);
      }}
      filterOptions={(x) => x}
    />
  );
};

export default CanadaPostAddress;
